import React from 'react'
import Reflux from 'reflux-react-16'
import _ from 'lodash'

import LoginActions from '../../../../actions/LoginActions.js'
import CustomerStore from '../../../../stores/CustomerStore.js'
import t from '../../../../helpers/Languages.js'

import {
    Alert,
    Badge,
    Button,
    H3,
    H6,
    IconButton,
    P,
    S2,
    Caption,
    Card,
    Row,
    Colors,
    ToolTip,
    Popup
} from '../../../../components/UI/index.js'
import {
    formSpacing,
    Input
} from '../../../../components/UI/forms/index.js'

import DefaultPartnerSettingsPanel from '../../../../components/resellers/DefaultPartnerSettingsPanel.js'
import PartnerSettingsPanel from '../../../../components/resellers/PartnerSettingsPanel.js'

const DIRECTION_ICONS = {outgoing: 'mdi mdi-arrow-top-right', incoming: 'mdi mdi-arrow-bottom-left'}

class PartnerSettings extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            defaultSettingsDirection: null,
            selectedPartnerId: null,
            selectedDirection: null,
            error: ''
        }
    }

    onSaveDefaultPartnerSettings(defaultPartnerSettings) {
        const reseller = {...this.props.reseller}

        reseller.settings.defaultPartnerSettings = defaultPartnerSettings

        this.props.onSave(reseller, 'defaultPartnerSettings')
    }

    /**
     * @param {'outgoing' | 'incoming'} direction
     * @param {object} partnerSettings
     */
    onSavePartnerSettings(direction, partnerSettings) {
        LoginActions.updatePartnerSettings(direction, partnerSettings, (err) => {
            this.setState({error: err || ''})
        })
    }

    /**
     * Sends a partner request. The popup asks for the partner's API key (unless given) and the direction of the
     * request: outgoing, incoming or both. For a known partner only the directions without a connection or request
     * are offered.
     * @param {string} [partnerKey] - the key of a known partner, for a new request
     * @param {'outgoing' | 'incoming' | 'both'} [direction] - preselected direction
     */
    async onAddPartner(partnerKey, direction = 'both') {
        const {reseller} = this.props

        const connected = (direction, key) => !!key && !!(direction === 'outgoing' ? reseller.settings.outgoingPartners : reseller.settings.incomingPartners)
            ?.some((connection) => connection.key === key && connection.status !== 'inactive')

        const directionOptions = [
            {value: 'both', title: t('partnerDirectionBoth'), directions: ['outgoing', 'incoming']},
            {value: 'outgoing', title: t('partnerDirectionOutgoing'), directions: ['outgoing']},
            {value: 'incoming', title: t('partnerDirectionIncoming'), directions: ['incoming']}
        ].filter((option) => !option.directions.some((direction) => connected(direction, partnerKey)))

        // A known partner is asked for a new request without entering its key again
        const keyInput = {type: 'text', name: 'partnerKey', label: t('partnerApiKeyLabel'), value: '', required: true}

        this.popup.setInputs([
            ...(partnerKey ? [] : [keyInput]),
            {
                type: 'select',
                name: 'direction',
                label: t('partnerDirectionLabel'),
                value: directionOptions.some((option) => option.value === direction) ? direction : directionOptions[0]?.value,
                options: directionOptions.map(({value, title}) => ({value, title})),
                noSort: true
            }
        ])

        const values = await this.popup.open(partnerKey ? t('partnerRequestTitle') : t('partnerAdd'), t('partnerRequestText'))
        if (!values) return

        const key = partnerKey || values.partnerKey.trim()

        const directions = directionOptions.find((option) => option.value === values.direction)?.directions || []

        LoginActions.addPartner(key, directions, (err) => {
            if (err) {
                this.popup.setError(err)
            } else {
                this.popup.close()
                this.setState({error: ''})
            }
        })
    }

    /**
     * @param {'outgoing' | 'incoming'} direction
     * @param {object} connection
     */
    renderConnection(direction, connection) {
        const {partners} = this.props
        const {selectedPartnerId, selectedDirection} = this.state

        const partner = partners?.[connection._id]
        const directionName = (direction === 'outgoing' ? t('partnerDirectionOutgoing') : t('partnerDirectionIncoming')).toLowerCase()

        if (!partner) {
            return null
        }

        const status = {
            active: {variant: 'success', label: t('partnerStatusActive')},
            requested: {variant: 'warning', label: t('partnerStatusRequested')},
            pending: {variant: 'info', label: t('partnerStatusPending')},
            inactive: {variant: 'error', label: connection.key ? t('partnerStatusInactive') : t('partnerStatusRejected')}
        }[connection.status]

        return (
            <Card
                shadow
                key={partner._id}
                style={{display: 'flex', alignItems: 'center', columnGap: formSpacing.columnGap, marginBottom: 8, minHeight: 56, padding: '8px 12px 8px 16px'}}
                selected={selectedPartnerId === partner._id && selectedDirection === direction}
            >
                <div style={{width: 48, display: 'flex', justifyContent: 'center', flexShrink: 0}}>
                    <img style={{objectFit: 'contain', height: 24, maxWidth: 48}} src={partner.logo} />
                </div>

                <P style={{flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{partner.name}</P>

                <Badge size='sm' variant={status.variant}>{status.label}</Badge>

                {connection.status === 'requested' &&
                    <Button
                        variant='text'
                        onClick={async () => {
                            if (!await this.popup.open(t('partnerCancelRequestTitle'), t('partnerCancelRequestText'))) return

                            LoginActions.removePartner(partner._id, direction, (err) => {
                                if (err) {
                                    this.popup.setError(err)
                                } else {
                                    this.popup.close()
                                }
                            })
                        }}
                    >
                        {t('partnerCancelRequest')}
                    </Button>
                }
                {connection.status === 'pending' &&
                    <>
                        <Button
                            variant='text'
                            onClick={async () => {
                                if (!await this.popup.open(t('partnerReceivedRequestTitle'), t('partnerAcceptRequestText', directionName))) return

                                LoginActions.handlePartnerRequest(partner._id, 'accepted', direction, (err) => {
                                    if (err) {
                                        this.popup.setError(err)
                                    } else {
                                        this.popup.close()
                                    }
                                })
                            }}
                        >
                            {t('partnerAcceptRequest')}
                        </Button>

                        <Button
                            variant='text'
                            onClick={async () => {
                                if (!await this.popup.open(t('partnerReceivedRequestTitle'), t('partnerRejectRequestText', directionName))) return

                                LoginActions.handlePartnerRequest(partner._id, 'rejected', direction, (err) => {
                                    if (err) {
                                        this.popup.setError(err)
                                    } else {
                                        this.popup.close()
                                    }
                                })
                            }}
                        >
                            {t('partnerRejectRequest')}
                        </Button>
                    </>
                }

                {connection.status === 'inactive' &&
                    <>
                        {connection.key &&
                            <Button
                                variant='text'
                                onClick={() => this.onAddPartner(connection.key, direction)}
                            >
                                {t('partnerNewRequest')}
                            </Button>
                        }

                        <Button
                            variant='text'
                            onClick={async () => {
                                if (!await this.popup.open(t('partnerRemoveTitle'), t('partnerRemoveText'))) return
                                LoginActions.handlePartnerRequest(partner._id, 'remove', direction, (err) => {
                                    if (err) {
                                        this.popup.setError(err)
                                    } else {
                                        this.popup.close()
                                    }
                                })
                            }}
                        >
                            {t('delete')}
                        </Button>

                    </>
                }

                {connection.status === 'active' &&
                    <Button
                        variant='text'
                        onClick={() => this.setState({defaultSettingsDirection: null, selectedPartnerId: partner._id, selectedDirection: direction})}
                    >
                        {t('settings')}
                    </Button>
                }
            </Card>
        )
    }

    /**
     * Adds a new delivery zone (drawn in one of the partner settings panels) to the reseller's own delivery zones
     * @param {object} geoJSON
     * @returns {Promise<object|undefined>} the new delivery zone
     */
    async onCreateDeliveryZone(geoJSON) {
        const reseller = {...this.props.reseller}
        reseller.settings = {...reseller.settings}
        reseller.settings.deliveryZones = [...reseller.settings.deliveryZones]

        this.popup.setInputs([{
            type: 'text',
            value: `${t('deliveryZone')} ${reseller.settings.deliveryZones.length + 1}`,
            name: 'name',
            label: t('name')
        }])

        const values = await this.popup.open(t('partnerNewDeliveryZoneTitle'), t('partnerNewDeliveryZoneText'))
        this.popup.close()

        if (!values?.name) {
            return
        }

        const deliveryZone = {
            id: '_' + Math.random().toString(36).substr(2, 9),
            name: values.name,
            color: Colors.routeColors[_.random(0, Colors.routeColors.length - 1)],
            geoJSON,
            visible: true
        }

        reseller.settings.deliveryZones.push(deliveryZone)

        this.props.onSave(reseller, 'deliveryZones')

        return deliveryZone
    }

    async onClickCopyPartnerKey() {
        if (navigator && navigator.clipboard) {
            try {
                await navigator.clipboard.writeText(this.props.reseller.settings.partnerKey)

                clearTimeout(this.tooltipTimeout)

                this.setState({showCopyTooltip: true})

                this.tooltipTimeout = setTimeout(() => this.setState({showCopyTooltip: false}), 2000)
            } catch (err) {
                console.log(err)
            }
        }
    }

    render() {
        const reseller = this.props.reseller
        const {partners} = this.props
        const {defaultSettingsDirection, selectedPartnerId, selectedDirection} = this.state

        return (
            <Row style={{gap: 24}}>
                <Card shadow style={{height: 'fit-content', flex: '1 1 0', minWidth: 600, maxWidth: 800, padding: 24}}>
                    <H3 style={{marginBottom: 36}}>{t('partnerConnections')}</H3>

                    <P style={{color: Colors.textDark}}>
                        {t('partnerIntro')}
                    </P>

                    <div style={{marginTop: 24, padding: 16, borderRadius: 8, background: Colors.backgroundNeutral, border: `1px solid ${Colors.grey30}`}}>
                        <S2 style={{marginBottom: 4}}>{t('partnerOwnKeyTitle')}</S2>
                        <Caption style={{color: Colors.textDark, marginBottom: 12}}>{t('partnerOwnKeyInfo')}</Caption>

                        <Row style={{alignItems: 'center', columnGap: formSpacing.columnGap}}>
                            <Input
                                style={{flex: 1}}
                                value={reseller.settings.partnerKey}
                                readOnly
                            />

                            <IconButton icon='mdi mdi-content-copy' tooltip={t('copy')} onClick={() => this.onClickCopyPartnerKey()} />
                        </Row>
                    </div>

                    <Button style={{width: '100%', marginTop: 12}} icon='mdi mdi-plus' label={t('partnerAdd')} onClick={() => this.onAddPartner()} />

                    <div style={{marginTop: 36}}>
                        <H6 style={{marginBottom: 4}}>{t('partnerDefaultSettings')}</H6>
                        <P style={{color: Colors.textDark, marginBottom: 12}}>{t('partnerDefaultSettingsInfo')}</P>

                        <Row style={{columnGap: formSpacing.columnGap}}>
                            {['outgoing', 'incoming'].map((direction) => (
                                <Button
                                    key={direction}
                                    style={{flex: 1}}
                                    variant={defaultSettingsDirection === direction ? 'outline' : 'outline-white'}
                                    icon={DIRECTION_ICONS[direction]}
                                    label={direction === 'outgoing' ? t('partnerOutgoingTitle') : t('partnerIncomingTitle')}
                                    onClick={() => this.setState({defaultSettingsDirection: direction, selectedPartnerId: null, selectedDirection: null})}
                                />
                            ))}
                        </Row>
                    </div>

                    {this.state.error &&
                        <Alert variant='danger' style={{marginBottom: 6, marginTop: 24}}>{this.state.error}</Alert>
                    }

                    {[
                        {direction: 'outgoing', title: t('partnerOutgoingTitle'), info: t('partnerOutgoingInfo'), connections: reseller.settings.outgoingPartners || []},
                        {direction: 'incoming', title: t('partnerIncomingTitle'), info: t('partnerIncomingInfo'), connections: reseller.settings.incomingPartners || []}
                    ].map((list) => ({
                        ...list,
                        // Connections with a reseller that no longer exists (or is archived) have no partner to show
                        connections: list.connections.filter((connection) => partners?.[connection._id])
                    })).filter(({connections}) => connections.length > 0).map(({direction, title, info, connections}) => (
                        <div key={direction} style={{marginTop: 48}}>
                            <H6 style={{marginBottom: 4}}>{title}</H6>
                            <P style={{color: Colors.textDark, marginBottom: 12}}>{info}</P>

                            {connections.map((connection) => this.renderConnection(direction, connection))}
                        </div>
                    ))}

                    <ToolTip visible={this.state.showCopyTooltip} text={t('copiedToClipboard')}></ToolTip>

                </Card>

                {selectedPartnerId &&
                    <PartnerSettingsPanel
                        reseller={reseller}
                        partnerId={selectedPartnerId}
                        direction={selectedDirection}
                        partners={partners}
                        customers={this.props.customers}
                        onClose={() => this.setState({selectedPartnerId: null, selectedDirection: null})}
                        onRemove={async (direction) => {
                            const partnerName = partners?.[selectedPartnerId]?.name || ''

                            if (!await this.popup.open(t('partnerDeactivateTitle'), direction === 'outgoing' ? t('partnerDeactivateOutgoingText', partnerName) : t('partnerDeactivateIncomingText', partnerName))) return

                            LoginActions.removePartner(selectedPartnerId, direction, (err) => {
                                if (err) {
                                    this.popup.setError(err)
                                } else {
                                    this.popup.close()
                                    this.setState({selectedPartnerId: null, selectedDirection: null})
                                }
                            })
                        }}
                        onSave={(partnerSettings) => this.onSavePartnerSettings(selectedDirection, partnerSettings)}
                        onCreateDeliveryZone={this.onCreateDeliveryZone.bind(this)}
                    />
                }

                {defaultSettingsDirection &&
                    <DefaultPartnerSettingsPanel
                        key={defaultSettingsDirection}
                        reseller={reseller}
                        direction={defaultSettingsDirection}
                        defaultPartnerSettings={reseller.settings.defaultPartnerSettings}
                        customers={this.props.customers}
                        onClose={() => this.setState({defaultSettingsDirection: null})}
                        onSave={(defaultPartnerSettings) => this.onSaveDefaultPartnerSettings(defaultPartnerSettings)}
                        onCreateDeliveryZone={this.onCreateDeliveryZone.bind(this)}
                    />
                }

                <Popup ref={(ref) => this.popup = ref} />
            </Row>

        )
    }
}

// The customers are needed for the condition Klant
class PartnerSettingsWrapper extends Reflux.Component {
    constructor(props) {
        super(props)

        this.store = CustomerStore

        this.state = {
            customers: {}
        }
    }

    render() {
        return <PartnerSettings {...this.props} customers={this.state.customers} />
    }
}

export default PartnerSettingsWrapper
